import type {
  ActionOutputData,
  RecordJobData,
} from "@smartsuite/automation-engine-api";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { translateAction } from "../../../../../locales/i18n";
import { useRootStore } from "../../../../../stores/hooks";
import type { ActionCandidate } from "../../../../../types/candidate";
import { ActionLabel } from "../../../../../utils/constants";
import {
  addEmptyValueIfNeeded,
  findRecordValue,
  getErrorMessage,
  getInputLabel,
} from "../../../ActivityGrid/ActivityGrid.helpers";
import { ActionError } from "../ActionError/ActionError";
import { ActivityDetailsGroup } from "../ActivityDetailsGroup/ActivityDetailsGroup";
import { ActivityDetailsItem } from "../ActivityDetailsItem/ActivityDetailsItem";
import { ActivityDetailsTriggerGroup } from "../ActivityDetailsTriggerGroup/ActivityDetailsTriggerGroup";

interface FindRecordsGroupProps {
  action: ActionOutputData;
  onDetailsClick?: () => void;
}

export const FindRecordsGroup = observer<FindRecordsGroupProps>(
  ({ action, onDetailsClick }) => {
    const { integrations, automations, jobDetails } = useRootStore();
    const selectedIntegration = integrations.list.find(
      (i) => i.integrationId === action.actionReference?.integrationId,
    );
    const selectedAction = selectedIntegration?.actions.find(
      (a) => a.actionId === action.actionReference?.actionId,
    );
    const [selectedActionFallback, setSelectedActionFallback] = useState<
      ActionCandidate | undefined
    >();
    useEffect(() => {
      setSelectedActionFallback(
        automations.candidate.allActions.find(
          (actionInput: ActionCandidate) =>
            actionInput.actionReference?.instanceId ===
            action.actionReference?.instanceId,
        ),
      );
    }, [automations.candidate]);
    const status = action.status.oneofKind === "ok" ? "is_success" : "is_error";

    const errorMessage = getErrorMessage(action, jobDetails.job);

    let recordJobsFiltered: RecordJobData[];
    const recordJobDatas = jobDetails.recordJobData;
    // for find-record with limit = 1
    if (action.recordList?.recordList !== undefined) {
      recordJobsFiltered = recordJobDatas.filter(
        (recordJobData) =>
          action.actionReference?.actionId ===
            recordJobData.actionReference?.actionId &&
          action.actionReference?.instanceId ===
            recordJobData.actionReference?.instanceId &&
          action.actionReference?.integrationId ===
            recordJobData.actionReference?.integrationId,
      );

      recordJobsFiltered = recordJobsFiltered.flatMap((item) => {
        if (
          item.outputData?.recordList?.recordList !== undefined &&
          item.outputData.recordList.recordList.records.length > 0
        ) {
          return [item];
        } else return [];
      });
    } else {
      recordJobsFiltered = recordJobDatas.filter((recordJobData) => {
        return (
          recordJobData.actionReference?.actionId !== ActionLabel.findRecords &&
          recordJobData.source?.source.oneofKind === "actionReference" &&
          recordJobData.source?.source.actionReference?.instanceId ===
            action.actionReference?.instanceId
        );
      });
    }
    let index = -1;

    const recordsCount = recordJobsFiltered.reduce((acc, recordJob) => {
      const records =
        recordJob.outputData?.recordList?.recordList !== undefined
          ? recordJob.outputData.recordList.recordList.records
          : [recordJob.record];
      return acc + records.length;
    }, 0);

    return (
      <ActivityDetailsGroup
        label={
          selectedAction !== undefined ? translateAction(selectedAction) : ""
        }
        subLabel={t("job-list.records-found", {
          count: recordsCount,
        })}
        status={status}
      >
        {status === "is_error" && <ActionError errorMessage={errorMessage} />}
        {recordJobsFiltered.flatMap((recordJob) => {
          const records =
            recordJob.outputData?.recordList?.recordList !== undefined
              ? recordJob.outputData.recordList.recordList.records
              : [recordJob.record];

          return records.map((record) => {
            index++;

            return (
              <ActivityDetailsTriggerGroup
                key={index}
                title={`Record ${index + 1}`}
                onClick={onDetailsClick}
              >
                <ActivityDetailsItem
                  label={getInputLabel(
                    {
                      inputId: "application_id",
                    },
                    jobDetails,
                    action,
                    automations.candidate.allActions,
                    selectedActionFallback,
                    index,
                  )}
                >
                  {findRecordValue("application_id", record)}
                </ActivityDetailsItem>
                <ActivityDetailsItem
                  label={getInputLabel(
                    {
                      inputId: "record_id",
                    },
                    jobDetails,
                    action,
                    automations.candidate.allActions,
                    selectedActionFallback,
                    index,
                  )}
                >
                  {findRecordValue("record_id", record)}
                </ActivityDetailsItem>
                <ActivityDetailsItem label={t("job-list.record-name")}>
                  {addEmptyValueIfNeeded(findRecordValue("title", record))}
                </ActivityDetailsItem>
              </ActivityDetailsTriggerGroup>
            );
          });
        })}
      </ActivityDetailsGroup>
    );
  },
);
